import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Box, CheckCircle2, X, Trophy, Users, Package, CircleDollarSign, ExternalLink, Eye, Loader2, MailWarning } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import GlbPreview, { AssetImage } from "@/components/creator/GlbPreview";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const mono = "font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500";

export default function AdminCreators() {
  const [tab, setTab] = useState("applications");
  const [apps, setApps] = useState([]);
  const [packs, setPacks] = useState([]);
  const [roster, setRoster] = useState([]);
  const [rejectReason, setRejectReason] = useState({});
  const [rejectNotes, setRejectNotes] = useState({});
  const [approveForm, setApproveForm] = useState({});
  const [viewerJob, setViewerJob] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/creators/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/admin/creators/packs").then((r) => setPacks(r.data)).catch(() => {});
    api.get("/admin/creators/roster").then((r) => setRoster(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(ok || r.data?.message || "Done");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const pending = apps.filter((a) => a.status === "pending");
  const inReview = packs.filter((p) => p.status === "in_review");

  return (
    <section data-testid="admin-creators-section" className="flex flex-col gap-4">
      <div className="flex gap-2 flex-wrap">
        {[["applications", `Applications (${pending.length})`, Users], ["packs", `Pack QA (${inReview.length})`, Package], ["roster", `Creators (${roster.length})`, Trophy]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`creators-tab-${id}`} onClick={() => setTab(id)} className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2.5 border transition-colors flex items-center gap-2 ${tab === id ? "border-nc-blue/60 text-nc-blue bg-nc-blue/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
        <button
          data-testid="creators-remind-button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const r = await api.post("/admin/creators/remind-inactive");
              toast.success(`${r.data.sent} reminder emails queued — ${r.data.total_inactive} creators inactive (72h cooldown per creator)`);
            } catch (err) {
              toast.error(formatApiErrorDetail(err.response?.data?.detail));
            } finally { setBusy(false); }
          }}
          className="font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2.5 border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 transition-colors flex items-center gap-2 ml-auto disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MailWarning className="w-3.5 h-3.5" />} Remind unpaid creators
        </button>
      </div>

      {tab === "applications" && (
        <div className="flex flex-col gap-3">
          {apps.length === 0 && <p data-testid="creator-apps-empty" className="font-mono text-[10px] text-zinc-600 border border-white/10 p-5">No creator applications yet.</p>}
          {apps.map((a, i) => (
            <div key={a.id} data-testid={`creator-app-${i + 1}`} className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-sans font-bold text-sm text-zinc-100">{a.name} <span className="font-mono text-[10px] text-zinc-500 font-normal">// {a.email}</span></p>
                  <p className={mono}>{String(a.created_at).slice(0, 10)} // tools: {(a.tools_used || []).join(", ") || "unspecified"}</p>
                </div>
                <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-0.5 ${a.status === "pending" ? "text-nc-orange border-nc-orange/40" : a.status === "approved" ? "text-nc-green border-nc-green/40" : "text-red-400 border-red-500/40"}`}>{a.status}</span>
              </div>
              {a.portfolio_url && (
                <a href={a.portfolio_url} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-nc-blue hover:text-zinc-100 flex items-center gap-1.5 transition-colors">
                  <ExternalLink className="w-3 h-3" /> {a.portfolio_url}
                </a>
              )}
              <div className="border border-white/5 bg-[#050505]/60 p-3">
                <p className={mono}>Experience</p>
                <p className="font-mono text-[11px] text-zinc-300 mt-1 leading-relaxed">{a.experience}</p>
              </div>
              <div className="border border-nc-blue/20 bg-nc-blue/5 p-3">
                <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-blue">Sample prompt</p>
                <p className="font-mono text-[11px] text-zinc-300 mt-1 leading-relaxed italic">"{a.sample_prompt}"</p>
              </div>
              {a.status === "pending" && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button data-testid={`creator-app-approve-${i + 1}`} disabled={busy} onClick={() => act(() => api.post(`/admin/creators/applications/${a.id}/approve`))} className="font-mono text-[9px] tracking-[0.15em] uppercase px-4 py-2 bg-nc-green hover:bg-nc-green/80 disabled:opacity-40 text-black transition-colors flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3" /> Approve & Grant Access
                  </button>
                  <input data-testid={`creator-app-reason-${i + 1}`} className={`${inputCls} flex-1 min-w-[200px]`} placeholder="Rejection reason (optional)…" value={rejectReason[a.id] || ""} onChange={(e) => setRejectReason((s) => ({ ...s, [a.id]: e.target.value }))} />
                  <button data-testid={`creator-app-reject-${i + 1}`} disabled={busy} onClick={() => act(() => api.post(`/admin/creators/applications/${a.id}/reject`, { reason: rejectReason[a.id] || "" }))} className="font-mono text-[9px] uppercase px-4 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1.5">
                    <X className="w-3 h-3" /> Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "packs" && (
        <div className="flex flex-col gap-3">
          {packs.length === 0 && <p data-testid="creator-packs-empty" className="font-mono text-[10px] text-zinc-600 border border-white/10 p-5">No packs submitted yet.</p>}
          {packs.map((p, i) => (
            <div key={p.id} data-testid={`qa-pack-${i + 1}`} className="border border-white/10 bg-black/40 p-5 flex flex-col gap-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  {p.cover_asset_id && <AssetImage assetId={p.cover_asset_id} alt="" className="w-16 h-16 object-cover border border-white/10" />}
                  <div>
                    <p className="font-sans font-bold text-sm text-zinc-100">{p.title}</p>
                    <p className={mono}>{p.creator_name} // {p.category} // {(p.job_ids || []).length} model(s)</p>
                  </div>
                </div>
                <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-0.5 ${p.status === "in_review" ? "text-nc-orange border-nc-orange/40" : p.status === "published" ? "text-nc-green border-nc-green/40" : "text-red-400 border-red-500/40"}`}>{p.status}</span>
              </div>
              <p className="font-mono text-[11px] text-zinc-400 leading-relaxed border border-white/5 bg-[#050505]/60 p-3">{p.description}</p>
              <div className="flex flex-wrap gap-2">
                {(p.job_ids || []).map((jid) => (
                  <button key={jid} data-testid={`qa-view-${jid}`} onClick={() => setViewerJob(viewerJob === jid ? "" : jid)} className={`font-mono text-[9px] px-2.5 py-1.5 border transition-colors flex items-center gap-1.5 ${viewerJob === jid ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-300 border-white/10 hover:border-white/30"}`}>
                    <Eye className="w-3 h-3" /> forge-{jid.slice(0, 8)}
                  </button>
                ))}
              </div>
              {viewerJob && (p.job_ids || []).includes(viewerJob) && (
                <div className="max-w-md"><GlbPreview jobId={viewerJob} /></div>
              )}
              {p.status === "in_review" && (
                <div className="border-t border-white/10 pt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-3">
                    <p className="font-mono text-[9px] tracking-[0.25em] text-nc-green uppercase">Approve & publish</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className={labelCls}>Bounty AUD</label>
                        <input data-testid={`qa-bounty-${i + 1}`} type="number" min="0" className={inputCls} placeholder="150" value={approveForm[p.id]?.bounty ?? ""} onChange={(e) => setApproveForm((s) => ({ ...s, [p.id]: { ...(s[p.id] || {}), bounty: e.target.value } }))} />
                      </div>
                      <div>
                        <label className={labelCls}>Royalty %</label>
                        <input data-testid={`qa-royalty-${i + 1}`} type="number" min="0" max="50" className={inputCls} placeholder="10" value={approveForm[p.id]?.royalty ?? ""} onChange={(e) => setApproveForm((s) => ({ ...s, [p.id]: { ...(s[p.id] || {}), royalty: e.target.value } }))} />
                      </div>
                      <div>
                        <label className={labelCls}>Price AUD</label>
                        <input data-testid={`qa-price-${i + 1}`} type="number" min="1" className={inputCls} placeholder="49" value={approveForm[p.id]?.price ?? ""} onChange={(e) => setApproveForm((s) => ({ ...s, [p.id]: { ...(s[p.id] || {}), price: e.target.value } }))} />
                      </div>
                    </div>
                    <button data-testid={`qa-approve-${i + 1}`} disabled={busy} onClick={() => act(() => api.post(`/admin/creators/packs/${p.id}/approve`, {
                      bounty_aud: parseFloat(approveForm[p.id]?.bounty) || 0,
                      royalty_pct: parseFloat(approveForm[p.id]?.royalty) || 0,
                      price_aud: parseFloat(approveForm[p.id]?.price) || 0,
                    }))} className="self-start font-mono text-[9px] tracking-[0.15em] uppercase px-4 py-2.5 bg-nc-green hover:bg-nc-green/80 disabled:opacity-40 text-black transition-colors flex items-center gap-1.5">
                      <CheckCircle2 className="w-3 h-3" /> Approve & Publish to Marketplace
                    </button>
                  </div>
                  <div className="flex flex-col gap-3">
                    <p className="font-mono text-[9px] tracking-[0.25em] text-red-400 uppercase">Reject with notes</p>
                    <textarea data-testid={`qa-notes-${i + 1}`} rows={2} className={`${inputCls} resize-none`} placeholder="What needs fixing…" value={rejectNotes[p.id] || ""} onChange={(e) => setRejectNotes((s) => ({ ...s, [p.id]: e.target.value }))} />
                    <button data-testid={`qa-reject-${i + 1}`} disabled={busy} onClick={() => act(() => api.post(`/admin/creators/packs/${p.id}/reject`, { notes: rejectNotes[p.id] || "" }))} className="self-start font-mono text-[9px] uppercase px-4 py-2.5 border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1.5">
                      <X className="w-3 h-3" /> Reject Pack
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "roster" && (
        <div className="flex flex-col gap-3">
          {roster.length === 0 && <p data-testid="creator-roster-empty" className="font-mono text-[10px] text-zinc-600 border border-white/10 p-5">No creators on the team yet.</p>}
          {roster.map((c, i) => (
            <div key={c.id} data-testid={`roster-${i + 1}`} className="border border-white/10 bg-black/40 px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-sans font-bold text-sm text-zinc-100">{c.name || c.email.split("@")[0]} <span className="font-mono text-[10px] text-zinc-500 font-normal">// {c.email}</span></p>
                <p className={mono}>{c.packs} pack(s) // {c.published} published // claimable AUD {c.claimable} // paid AUD {c.paid}</p>
              </div>
              <button data-testid={`roster-dispatch-${i + 1}`} disabled={busy} onClick={() => act(() => api.post("/admin/creators/payouts/dispatch", { creator_id: c.id }))} className="font-mono text-[9px] tracking-[0.15em] uppercase px-4 py-2 border border-nc-green/50 text-nc-green hover:bg-nc-green/10 disabled:opacity-40 transition-colors flex items-center gap-1.5">
                <CircleDollarSign className="w-3 h-3" /> Dispatch Payout
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
